import React from 'react';
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	FormHelperText,
	FormLabel,
	InputAdornment,
	FormControl,
	MenuItem,
	Pagination,
	Paper,
	Select,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableRow,
	TextField,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import { useAuth } from '../../../context/AuthContext';
import { API_BASE_URL } from '../../../config/apiConfig';
import type { UploadedEmployee, ApprovedBranch } from '../../../hooks/useEmployeeHandlers';
import { EMPLOYMENT_TYPE_OPTIONS, RESIDENCY_STATUS_OPTIONS } from '../../../hooks/useEmployeeHandlers';
import UploadPreviewMobile from '../mobile/UploadPreviewMobile';

// ─── Validation ──────────────────────────────────────────────────────────────

export interface UploadedEmployeeErrors {
	branch?: string;
	firstName?: string;
	lastName?: string;
	personalNo?: string;
	email?: string;
	phone?: string;
	employmentType?: string;
	taxId?: string;
	residencyStatus?: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\d{6,12}$/;

function validateUploadedEmployee(
	emp: UploadedEmployee,
	loadedBranches: ApprovedBranch[],
): UploadedEmployeeErrors {
	const errors: UploadedEmployeeErrors = {};
	if (loadedBranches.length > 0 && !loadedBranches.some((b) => b.id === emp.branch)) {
		errors.branch = i18n.t('employee.upload.validation.branchRequired');
	}
	if (!(emp.firstName || '').trim()) errors.firstName = i18n.t('employee.upload.validation.firstNameRequired');
	if (!(emp.lastName || '').trim()) errors.lastName = i18n.t('employee.upload.validation.lastNameRequired');
	if (!(emp.personalNo || '').trim()) errors.personalNo = i18n.t('employee.upload.validation.personalNoRequired');
	if (!(emp.email || '').trim()) {
		errors.email = i18n.t('employee.upload.validation.emailRequired');
	} else if (!EMAIL_REGEX.test(emp.email)) {
		errors.email = i18n.t('employee.upload.validation.emailInvalid');
	}
	if (!(emp.phone || '').trim()) {
		errors.phone = i18n.t('employee.upload.validation.phoneRequired');
	} else if (!PHONE_REGEX.test(emp.phone)) {
		errors.phone = i18n.t('employee.upload.validation.phoneFormat');
	}
	if (!(emp.employmentType || '').trim()) errors.employmentType = i18n.t('employee.upload.validation.employmentTypeRequired');
	if (!(emp.residencyStatus || '').trim()) errors.residencyStatus = i18n.t('employee.upload.validation.residencyStatusRequired');
	return errors;
}

interface UploadPreviewDialogProps {
	open: boolean;
	employees: UploadedEmployee[];
	onClose: () => void;
	onSave: () => void;
	onChange: (rowId: number, field: keyof UploadedEmployee, value: any) => void;
	saveLoading?: boolean;
	saveError?: { message: string; violations: string[] } | null;
}

type FieldDef =
	| { labelKey: string; field: keyof UploadedEmployee; type: 'text' }
	| { labelKey: string; field: keyof UploadedEmployee; type: 'switch' }
	| { labelKey: string; field: keyof UploadedEmployee; type: 'branch' }
	| { labelKey: string; field: keyof UploadedEmployee; type: 'employmentType' }
	| { labelKey: string; field: keyof UploadedEmployee; type: 'residencyStatus' };

const FIELDS: FieldDef[] = [
	{ labelKey: 'hr.jobs.table.header.branch', field: 'branch', type: 'branch' },
	{ labelKey: 'employee.form.field.firstName', field: 'firstName', type: 'text' },
	{ labelKey: 'employee.form.field.lastName', field: 'lastName', type: 'text' },
	{ labelKey: 'employee.form.field.personalNo', field: 'personalNo', type: 'text' },
	{ labelKey: 'employee.form.field.email', field: 'email', type: 'text' },
	{ labelKey: 'employee.form.field.phone', field: 'phone', type: 'text' },
	{ labelKey: 'employee.form.field.employmentType', field: 'employmentType', type: 'employmentType' },
	{ labelKey: 'employee.form.field.taxId', field: 'taxId', type: 'text' },
	{ labelKey: 'employee.form.section.residencyStatus', field: 'residencyStatus', type: 'residencyStatus' },
	{ labelKey: 'employee.form.activateEmployee', field: 'status', type: 'switch' },
	{ labelKey: 'employee.upload.field.citizen', field: 'isCitizen', type: 'switch' },
	{ labelKey: 'employee.form.taxExempt', field: 'isTaxEmpt', type: 'switch' },
];

const UploadPreviewDialog: React.FC<UploadPreviewDialogProps> = ({
	open,
	employees,
	onClose,
	onSave,
	onChange,
	saveLoading = false,
	saveError = null,
}) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
	const { token } = useAuth();

	const [page, setPage] = React.useState(1);
	const [search, setSearch] = React.useState('');
	const [branches, setBranches] = React.useState<ApprovedBranch[]>([]);
	const [branchesLoading, setBranchesLoading] = React.useState(false);
	const [showAllErrors, setShowAllErrors] = React.useState(false);

	// Fetch branches once when dialog opens
	React.useEffect(() => {
		if (!open || !token) return;
		setBranchesLoading(true);
		fetch(`${API_BASE_URL}/api/branches/get?status=APPROVED`, {
			headers: { Authorization: `Bearer ${token}` },
		})
			.then((res) => res.json())
			.then((data) => {
				const loaded: ApprovedBranch[] = data.tenants ?? [];
				setBranches(loaded);
				// Auto-resolve branch names (from Excel) to branch ids
				employees.forEach((emp) => {
					const alreadyId = loaded.some((b) => b.id === emp.branch);
					if (!alreadyId) {
						const match = loaded.find(
							(b) => b.name.toLowerCase() === (emp.branch ?? '').toLowerCase()
						);
						if (match) onChange(emp._rowId, 'branch', match.id);
					}
				});
			})
			.catch(() => setBranches([]))
			.finally(() => setBranchesLoading(false));
	}, [open, token]);

	React.useEffect(() => {
		if (open) {
			setPage(1);
			setSearch('');
			setShowAllErrors(false);
		}
	}, [open, employees.length]);

	const filtered = React.useMemo(() => {
		const q = search.trim().toLowerCase();
		if (!q) return employees;
		return employees.filter((emp) =>
			[emp.firstName, emp.lastName, emp.branch, emp.email, emp.phone,
			emp.personalNo, emp.taxId, emp.employmentType, emp.residencyStatus]
				.some((v) => (v ?? '').toLowerCase().includes(q))
		);
	}, [employees, search]);

	React.useEffect(() => { setPage(1); }, [filtered.length]);

	const totalPages = filtered.length;
	const emp = filtered[page - 1];

	// Per-employee validation errors (only active when showAllErrors is true)
	const currentErrors: UploadedEmployeeErrors = React.useMemo(
		() => (emp && showAllErrors ? validateUploadedEmployee(emp, branches) : {}),
		[emp, branches, showAllErrors],
	);

	const invalidCount = React.useMemo(
		() =>
			showAllErrors
				? employees.filter((e) => Object.keys(validateUploadedEmployee(e, branches)).length > 0).length
				: 0,
		[employees, branches, showAllErrors],
	);

	const handleSaveClick = () => {
		const firstInvalid = employees.find(
			(e) => Object.keys(validateUploadedEmployee(e, branches)).length > 0,
		);
		if (firstInvalid) {
			setShowAllErrors(true);
			setSearch('');
			const idx = employees.findIndex((e) => e._rowId === firstInvalid._rowId);
			if (idx >= 0) setPage(idx + 1);
			return;
		}
		onSave();
	};

	// Returns true when the branch value is a known branch id
	const isBranchValid = (branchValue: string) =>
		branches.length === 0 || branches.some((b) => b.id === branchValue);

	const renderBranchCell = (row: UploadedEmployee) => {
		const valid = isBranchValid(row.branch);
		const displayName = branches.find((b) => b.id === row.branch)?.name ?? row.branch;
		const hasError = !valid && (branches.length > 0 || (showAllErrors && !row.branch));
		return (
			<FormControl fullWidth size="small" variant="standard" error={hasError}>
				<Select
					value={valid ? row.branch : ''}
					displayEmpty
					onChange={(e) => onChange(row._rowId, 'branch', e.target.value)}
					startAdornment={
						branchesLoading ? (
							<InputAdornment position="start">
								<CircularProgress size={14} />
							</InputAdornment>
						) : null
					}
					renderValue={(val) => val ? (branches.find((b) => b.id === val)?.name ?? String(val)) : <em style={{ color: '#aaa' }}>{t('employee.upload.selectBranchPlaceholder')}</em>}
				>
					{branches.map((b) => (
						<MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
					))}
				</Select>
				{hasError && (
					<FormHelperText>
						{currentErrors.branch ?? t('employee.upload.branchNotFound', {name: displayName})}
					</FormHelperText>
				)}
			</FormControl>
		);
	};

	const renderValue = (f: FieldDef, row: UploadedEmployee) => {
		if (f.type === 'branch') return renderBranchCell(row);

		if (f.type === 'employmentType') {
			const empTypeError = showAllErrors ? currentErrors.employmentType : undefined;
			return (
				<FormControl fullWidth size="small" variant="standard" error={!!empTypeError}>
					<Select
						value={EMPLOYMENT_TYPE_OPTIONS.includes(row.employmentType as any) ? row.employmentType : ''}
						displayEmpty
						onChange={(e) => onChange(row._rowId, 'employmentType', e.target.value)}
						renderValue={(val) => val || <em style={{ color: '#aaa' }}>{t('employee.upload.selectTypePlaceholder')}</em>}
					>
						{EMPLOYMENT_TYPE_OPTIONS.map((opt) => (
							<MenuItem key={opt} value={opt}>{opt}</MenuItem>
						))}
					</Select>
					{empTypeError && <FormHelperText>{empTypeError}</FormHelperText>}
				</FormControl>
			);
		}

		if (f.type === 'residencyStatus') {
			const residencyError = showAllErrors ? currentErrors.residencyStatus : undefined;
			return (
				<FormControl component="fieldset" fullWidth error={!!residencyError}>
					{RESIDENCY_STATUS_OPTIONS.map((opt) => (
						<FormControlLabel
							key={opt}
							control={
								<Switch
									checked={row.residencyStatus === opt}
									onChange={(e) => onChange(row._rowId, 'residencyStatus', e.target.checked ? opt : '')}
									size="small"
								/>
							}
							label={opt}
						/>
					))}
					{residencyError && <FormHelperText>{residencyError}</FormHelperText>}
				</FormControl>
			);
		}

		if (f.type === 'switch') {
			const checked = f.field === 'status'
				? (row[f.field] as boolean) !== false
				: (row[f.field] as boolean);
			return (
				<FormControlLabel
					control={
						<Switch
							checked={checked}
							onChange={(e) => onChange(row._rowId, f.field, e.target.checked)}
							size="small"
						/>
					}
					label=""
					sx={{ m: 0 }}
				/>
			);
		}

		const fieldError = showAllErrors
			? currentErrors[f.field as keyof UploadedEmployeeErrors]
			: undefined;
		return (
			<TextField
				value={row[f.field] as string}
				size="small"
				variant="standard"
				fullWidth
				onChange={(e) => onChange(row._rowId, f.field, e.target.value)}
				inputProps={{ 'aria-label': t(f.labelKey) }}
				error={!!fieldError}
				helperText={fieldError}
			/>
		);
	};

	return (
		<Dialog
			open={open}
			onClose={saveLoading ? undefined : onClose}
			fullScreen={fullScreen}
			maxWidth="sm"
			fullWidth
			PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 2 } }}
		>
			<DialogTitle sx={{ fontWeight: 700 }}>
				{t('employee.upload.dialogTitle')}
				{totalPages > 0 && (
					<Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
						{search.trim()
							? t('employee.upload.pageIndicatorFiltered', {page, totalPages, total: employees.length})
							: t('employee.upload.pageIndicator', {page, totalPages})}
					</Typography>
				)}
			</DialogTitle>

			<DialogContent dividers sx={{ p: isMobile ? 2 : 3 }}>
				<TextField
					fullWidth
					size="small"
					placeholder={t('employee.upload.searchPlaceholder')}
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					sx={{ mb: 2 }}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon fontSize="small" color="action" />
							</InputAdornment>
						),
						endAdornment: search ? (
							<InputAdornment position="end">
								<ClearIcon
									fontSize="small"
									sx={{ cursor: 'pointer', color: 'text.secondary' }}
									onClick={() => setSearch('')}
								/>
							</InputAdornment>
						) : null,
					}}
				/>

				{saveError && (
					<Alert severity="error" sx={{ mb: 2 }}>
						<Typography variant="body2" fontWeight="bold">{saveError.message}</Typography>
						{saveError.violations.length > 0 && (
							<ul style={{ margin: '4px 0 0', paddingLeft: '1.25rem' }}>
								{saveError.violations.map((v, i) => (
									<li key={i}>{v}</li>
								))}
							</ul>
						)}
					</Alert>
				)}

				{showAllErrors && invalidCount > 0 && (
					<Alert severity="warning" sx={{ mb: 2 }}>
						<Typography variant="body2" fontWeight="bold">
							{t('employee.upload.invalidCount', {count: invalidCount})}
						</Typography>
						<Typography variant="body2">
							{t('employee.upload.fixHighlighted')}
						</Typography>
					</Alert>
				)}

				{!emp ? (
					<Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
						{search.trim() ? t('employee.table.noResultsFor', {query: search}) : t('employee.upload.noEmployeesToPreview')}
					</Typography>
				) : isMobile ? (
					<UploadPreviewMobile
						employees={[emp]}
						onChange={onChange}
						branches={branches}
						branchesLoading={branchesLoading}
						errors={currentErrors}
						showAllErrors={showAllErrors}
					/>
				) : (
					<TableContainer component={Paper} variant="outlined">
						<Table size="small">
							<TableBody>
								{FIELDS.map((f) => (
									<TableRow key={String(f.field)}>
										<TableCell sx={{ fontWeight: 'bold', width: '40%', whiteSpace: 'nowrap' }}>
											{t(f.labelKey)}
										</TableCell>
										<TableCell>{renderValue(f, emp)}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				)}

				{totalPages > 1 && (
					<Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
						<Pagination
							color="primary"
							count={totalPages}
							page={page}
							onChange={(_, value) => setPage(value)}
						/>
					</Box>
				)}
			</DialogContent>

			<DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
				<Button variant="outlined" color="inherit" startIcon={<CloseIcon />} onClick={onClose} disabled={saveLoading}>
					{t('common.actions.close')}
				</Button>
				<Button
					variant="contained"
					color="primary"
					startIcon={saveLoading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
					onClick={handleSaveClick}
					disabled={saveLoading}
				>
					{t('common.actions.save')}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default UploadPreviewDialog;
